import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';

import type { BusinessUnitConversationTaggingConfig } from '../../../services/types/businessUnit';
import type { ExtendedLead } from '../../../services/types/lead';
import {
  FINANCIAL_INSTITUTION_BUSINESS_UNIT_IDS,
  INSUREX_BUSINESS_UNIT_ID,
  KOVACS_BUSINESS_UNIT_ID,
} from '../../../utils/constants';
import getAdvancePercentage, { getProgressColor, getProgressTextColor } from '../../../utils/getAdvancePercentage';

const INTERESTING_BG = 'rgba(249, 208, 10, 0.08)';
export const PENDING_CHAT_BG = '#DDF6FF';
export const PENDING_CHAT_COUNT_COLOR = '#42ABC3';

const StyledLeadCard = styled(Card)<{ cardbg?: string }>(({ cardbg }) => ({
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  backgroundColor: cardbg || '#ffffff',
  borderRadius: 12,
  boxShadow: '0px 2px 8px rgba(21, 24, 58, 0.04)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0px 12px 32px rgba(21, 24, 58, 0.06)',
  },
}));

const formatTimeAgo = (date: Date): string => {
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - new Date(date).getTime()) / (1000 * 60));

  if (diffInMinutes < 60) return `${diffInMinutes}m`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h`;

  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays}d`;
};

/**
 * Formats a source URL for better display
 * @param source - The source string (could be a URL or simple text)
 * @returns Formatted source for display
 */
const formatSourceForDisplay = (source: string): string => {
  if (!source) return '';

  // If it's a URL, extract the domain and first few path segments
  if (source.startsWith('http') || source.startsWith('@whatsapp-http')) {
    try {
      // Remove @whatsapp- prefix if present
      const cleanUrl = source.replace(/^@whatsapp-/, '');
      const url = new URL(cleanUrl);

      // Get domain and first path segment
      const domain = url.hostname;
      const pathSegments = url.pathname.split('/').filter(Boolean);
      const firstPath = pathSegments.length > 0 ? `/${pathSegments[0]}` : '';

      // Add ellipsis if there are query parameters or more path segments
      const hasMore = url.search || pathSegments.length > 1;
      return `${domain}${firstPath}${hasMore ? '...' : ''}`;
    } catch {
      return source.length > 25 ? `${source.substring(0, 25)}...` : source;
    }
  }

  return source.length > 25 ? `${source.substring(0, 25)}...` : source;
};

export const isChatPendingFromSalesman = (lead: ExtendedLead): boolean => {
  if (lead.status === 'PENDING_TO_BE_HANDLED') {
    const previousStatus = lead.statusHistory?.[lead.statusHistory.length - 2].status;
    if (previousStatus === 'HANDLED_BY_SALESMAN' && lead.client?.lastSentMessageRole === 'assistant') {
      return false;
    }
    return true;
  }
  if (lead.status === 'HANDLED_BY_SALESMAN' && lead.client?.lastSentMessageRole === 'user') return true;
  return false;
};

const getCardBackground = (lead: ExtendedLead): string | undefined => {
  if (lead.status === 'INTERESTING_FOR_SALESMAN') return INTERESTING_BG;
  if (isChatPendingFromSalesman(lead)) return PENDING_CHAT_BG;
  return undefined;
};

interface LeadCardProps {
  lead: ExtendedLead;
  onCardClick: (clientId: number) => void;
  showPurchasedIcon?: boolean;
  businessUnitId: number | null;
  conversationTaggingConfig?: BusinessUnitConversationTaggingConfig | null;
}

const LeadCard: FC<LeadCardProps> = ({ lead, onCardClick, businessUnitId, conversationTaggingConfig }) => {
  const clientName =
    lead.client?.firstName && lead.client?.lastName
      ? `${lead.client.firstName} ${lead.client.lastName}`
      : lead.client?.phone || 'Sin nombre';

  const isMueveSeguro =
    businessUnitId === INSUREX_BUSINESS_UNIT_ID || lead.client?.businessUnitId === INSUREX_BUSINESS_UNIT_ID;
  const isKovacs =
    businessUnitId === KOVACS_BUSINESS_UNIT_ID || lead.client?.businessUnitId === KOVACS_BUSINESS_UNIT_ID;
  const isFinancialInstitution =
    businessUnitId !== null && FINANCIAL_INSTITUTION_BUSINESS_UNIT_IDS.includes(businessUnitId);

  const brandOfInterest = lead.modelsOfInterest?.[0]?.brand || lead.initialBrandOfInterest || '';
  const modelOfInterest = lead.modelsOfInterest?.[0]?.model || lead.initialModelOfInterest || '';
  const carInfo = [brandOfInterest, modelOfInterest].filter(Boolean).join(' ') || null;
  const loanProtectionCount = getAdvancePercentage(lead, 'loanProtectionInsurance');
  const unemploymentCount = getAdvancePercentage(lead, 'unemploymentInsurance');
  const desgravamenCompletion = loanProtectionCount !== undefined ? `${loanProtectionCount}%` : '-';
  const cesantiaCompletion = unemploymentCount !== undefined ? `${unemploymentCount}%` : '-';

  const topics = lead.topics || [];

  const tagsContainerRef = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: topics.length affects container overflow
  useLayoutEffect(() => {
    if (tagsContainerRef.current) {
      setOverflows(tagsContainerRef.current.scrollWidth > tagsContainerRef.current.clientWidth);
    }
  }, [topics.length]);

  const cardBg = getCardBackground(lead);

  return (
    <StyledLeadCard cardbg={cardBg} onClick={() => onCardClick(lead.client?.id || 0)}>
      <CardContent sx={{ p: 1.25, display: 'flex', flexDirection: 'column', '&:last-child': { pb: 1.25 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, minHeight: 56 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
            {lead.source && (
              <Typography
                variant="caption"
                noWrap
                sx={{ fontSize: '0.7rem', color: '#5B8EC9', fontWeight: 500, fontFamily: 'Jost' }}
              >
                {formatSourceForDisplay(lead.source)}
              </Typography>
            )}

            {lead.origin && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, minWidth: 0 }}>
                <Chip
                  label={lead.origin}
                  size="small"
                  sx={{
                    fontSize: '0.6rem',
                    height: 18,
                    backgroundColor: 'rgba(21, 24, 58, 0.06)',
                    color: '#15183A',
                    fontWeight: 600,
                    fontFamily: 'Jost',
                    '& .MuiChip-label': { px: 0.75 },
                    flexShrink: 0,
                  }}
                />
                {lead.subOrigin && (
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{ fontSize: '0.65rem', color: '#15183A', opacity: 0.55, fontFamily: 'Jost' }}
                  >
                    {lead.subOrigin}
                  </Typography>
                )}
              </Box>
            )}

            <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: '#15183A', mt: 0.25, fontFamily: 'Jost' }}>
              {clientName}
            </Typography>

            {isMueveSeguro ? (
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                <Chip
                  label={`Desgravamen: ${desgravamenCompletion}`}
                  size="small"
                  sx={{
                    fontSize: '0.55rem',
                    height: 18,
                    backgroundColor:
                      loanProtectionCount !== undefined ? getProgressColor(loanProtectionCount) : '#f5f5f5',
                    color: loanProtectionCount !== undefined ? getProgressTextColor(loanProtectionCount) : '#15183A',
                    '& .MuiChip-label': { px: 0.5 },
                  }}
                />
                <Chip
                  label={`Cesantia: ${cesantiaCompletion}`}
                  size="small"
                  sx={{
                    fontSize: '0.55rem',
                    height: 18,
                    backgroundColor: unemploymentCount !== undefined ? getProgressColor(unemploymentCount) : '#f5f5f5',
                    color: unemploymentCount !== undefined ? getProgressTextColor(unemploymentCount) : '#15183A',
                    '& .MuiChip-label': { px: 0.5 },
                  }}
                />
              </Box>
            ) : (
              !isFinancialInstitution &&
              carInfo && (
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ color: '#15183A', opacity: 0.6, mt: 0.25, fontFamily: 'Jost' }}
                >
                  {carInfo}
                </Typography>
              )
            )}
          </Box>

          {/* Right column: time → messages */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
            <Typography
              variant="caption"
              sx={{ fontSize: '0.7rem', color: '#15183A', opacity: 0.5, fontFamily: 'Jost' }}
            >
              {formatTimeAgo(lead.client?.lastMessageDate || lead.createdAt)}
            </Typography>

            {(lead.messagesCount || 0) > 0 && (
              <Typography
                variant="caption"
                sx={{ fontSize: '0.7rem', color: '#15183A', opacity: 0.5, mt: 0.25, fontFamily: 'Jost' }}
              >
                💬 {lead.messagesCount}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Bottom row: tags */}
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flex: 1, minWidth: 0 }}>
            {isKovacs && lead.AIComment && (
              <Tooltip title={lead.AIComment} placement="top">
                <Chip
                  label="IA"
                  size="small"
                  sx={{
                    fontSize: '0.6rem',
                    height: 20,
                    backgroundColor: 'rgba(91, 142, 201, 0.14)',
                    color: '#2C5889',
                    fontWeight: 700,
                    '& .MuiChip-label': { px: 0.75 },
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            )}
            {topics.length > 0 && (
              <>
                <Box
                  ref={tagsContainerRef}
                  sx={{ display: 'flex', gap: 0.5, overflow: 'hidden', flexWrap: 'nowrap', flex: 1 }}
                >
                  {topics.map((topic) => {
                    const topicConfig = conversationTaggingConfig?.topics?.find((t) => t.name === topic);
                    const topicColor = topicConfig?.color;
                    return (
                      <Chip
                        key={topic}
                        label={topic}
                        size="small"
                        sx={{
                          fontSize: '0.6rem',
                          height: 20,
                          backgroundColor: topicColor ? `${topicColor}22` : 'rgba(21, 24, 58, 0.05)',
                          color: topicColor ?? 'rgba(21, 24, 58, 0.55)',
                          fontWeight: 600,
                          fontFamily: 'Jost',
                          letterSpacing: '0.03em',
                          textTransform: 'uppercase',
                          '& .MuiChip-label': { px: 0.75 },
                          flexShrink: 0,
                        }}
                      />
                    );
                  })}
                </Box>
                {overflows && (
                  <Tooltip title={topics.join(', ')} placement="top">
                    <Typography
                      variant="caption"
                      sx={{
                        cursor: 'pointer',
                        color: '#15183A',
                        opacity: 0.5,
                        flexShrink: 0,
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        ml: 0.5,
                        mr: 0.5,
                      }}
                    >
                      ...
                    </Typography>
                  </Tooltip>
                )}
              </>
            )}
          </Box>
        </Box>
      </CardContent>
    </StyledLeadCard>
  );
};

export default LeadCard;
